import {
  doubleArrayTableInputHelper,
  genericPlot,
  singleArrayTableInputHelper,
} from "../../../components/generic-financial-model/HelperFunctions";
import { InputSliderTable } from "../../../components/generic-financial-model/InputSliderTable";
import {
  DataEncoding,
  OutputItem,
  ParentChildInterface,
} from "../../../services/types/gallus";
import {
  dataLocation,
  kpiMetadata,
  chartData,
  chartGroupData,
  plKPIGroups,
  bsKPIGroups,
  chartInfo,
} from "./CelinkFinancialModelStaticData";

const outerDiv = (
  contents: JSX.Element,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  inputsArrayIndex: number,
  stateIndex: number,
) => {
  return (
    <div className="bordered-object input-sliders-table">
      <button
        className="btn me-2"
        onClick={() => handleCloseFunction(inputsArrayIndex, stateIndex)}
      >
        X
      </button>
      {contents}
    </div>
  );
};

export const PLExHudAndPLHuDTable = (
  title: string,
  data: (number | null)[][],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={doubleArrayTableInputHelper(
        year,
        data,
        ["Per Loan", "% Growth", "Fixed"],
        [
          "Tech",
          "Project Work",
          "Reserves",
          "Other",
          "Professional Fees",
          "G&A",
          "Tax",
        ],
        [
          { first: "", second: "" },
          { first: "", second: "%" },
          { first: "$", second: "" },
        ],
      )}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const HeadcountAndNoTitleTable = (
  title: string,
  data: (number | null)[][],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={doubleArrayTableInputHelper(
        year,
        data,
        ["Onshore FTEs", "Onshore Cost per FTE", "Offshore FTEs"],
        [
          "Borrower Care",
          "Client Services",
          "Executive",
          "Finance",
          "Human Resources",
          "Information Technology",
          "Legal",
          "PMO",
          "Risk and Compliance",
          "Servicing Operations",
        ],
        [
          { first: "", second: "" },
          { first: "$", second: "" },
          { first: "", second: "" },
        ],
      )}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const PeerTable = (
  title: string,
  data: (number | null)[][],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={doubleArrayTableInputHelper(
        year,
        data,
        ["Per Loan", "% Growth", "Fixed"],
        [
          "Compensation",
          "Contingent Labor",
          "Other",
          "Tech (Ex. Project Work)",
          "Project Work",
          "Reserves",
          "Other",
          "Professional Fees",
          "G&A",
          "Tax",
        ],
        [
          { first: "", second: "" },
          { first: "", second: "%" },
          { first: "$", second: "" },
        ],
      )}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const ExpenseReductionTable = (
  title: string,
  data: (number | null)[],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={singleArrayTableInputHelper(
        year,
        data,
        ["Onshore FTEs", "Other Reductions"],
        ["2024 Expense Reduction"],
        [
          { first: "", second: "" },
          { first: "$", second: "" },
        ],
      )}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const PortfolioTable = (
  title: string,
  data: (number | null)[][],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={doubleArrayTableInputHelper(
        year,
        data,
        ["Boarded / month", "Run-Off", "Rev. Per Loan"],
        [
          "AAG",
          "CMS",
          "FAR",
          "LBF",
          "Liberty",
          "MAM",
          "MOO",
          "RMF",
          "USB",
          "All Other",
          "HUD",
        ],
        [
          { first: "", second: "" },
          { first: "", second: "%" },
          { first: "$", second: "" },
        ],
      )}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const WorkingCapitalTable = (
  title: string,
  data: (number | null)[][],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={doubleArrayTableInputHelper(
        year,
        data,
        [""],
        [
          "Acc. Rec. Days",
          "Other Rec. Days",
          "Pre-Paid Ex. Days",
          "Acc. Pay. Days",
          "Acc. Exp. Days",
          "Other Curr. Liabilities",
        ],
        [{ first: "", second: "" }],
      )}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const OtherTable = (
  title: string,
  data: (number | null)[][],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={[
        {
          columnTitle: "",
          data: [
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "D&A",
              startingValue: data[year - 2024][0],
            },
            {
              firstSymbol: "",
              secondSymbol: "",
              label: "CapEx (Fixed Assets)",
              startingValue: data[year - 2024][1],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Min Cash",
              startingValue: data[year - 2024][2],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Monthly Debt Repay",
              startingValue: data[year - 2024][3],
            },
            {
              firstSymbol: "",
              secondSymbol: "%",
              label: "Interest Rate",
              startingValue: data[year - 2024][4],
            },
          ],
        },
      ]}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

export const DividendsAndCapExTable = (
  title: string,
  toPeer: (number | null)[],
  fromPeer: (number | null)[],
  capEx: (number | null)[],
  year: number,
  handleChangeFunction: (value: ParentChildInterface) => void,
  handleCloseFunction: (
    indexOfOpenedInput: number,
    indexOfState: number,
  ) => void,
  indexOfState: number,
  inputsArrayIndex: number,
) => {
  return outerDiv(
    <InputSliderTable
      columns={[
        {
          columnTitle: "Dividends To Peer",
          data: [
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Sep-24",
              startingValue: toPeer[0],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Oct-24",
              startingValue: toPeer[1],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Nov-24",
              startingValue: toPeer[2],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Dec-24",
              startingValue: toPeer[3],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Jan-25",
              startingValue: toPeer[4],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Feb-25",
              startingValue: toPeer[5],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Mar-25",
              startingValue: toPeer[6],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Apr-25",
              startingValue: toPeer[7],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "May-25",
              startingValue: toPeer[8],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Jun-25",
              startingValue: toPeer[9],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Dec-25",
              startingValue: toPeer[10],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Dec-26",
              startingValue: toPeer[11],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Dec-27",
              startingValue: toPeer[12],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Dec-28",
              startingValue: toPeer[13],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: "Dec-29",
              startingValue: toPeer[14],
            },
          ],
        },
        {
          columnTitle: "Div. From Peer to SHs",
          data: [
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[0],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[1],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[2],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[3],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[4],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[5],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[6],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[7],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[8],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[9],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[10],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[11],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[12],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[13],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: fromPeer[14],
            },
          ],
        },
        {
          columnTitle: "CapEx",
          data: [
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[0],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[1],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[2],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[3],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[4],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[5],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[6],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[7],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[8],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[9],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[10],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[11],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[12],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[13],
            },
            {
              firstSymbol: "$",
              secondSymbol: "",
              label: null,
              startingValue: capEx[14],
            },
          ],
        },
      ]}
      onNewValuesChange={handleChangeFunction}
      stateIndex={{ indexOfState: indexOfState, indexOfSubArray: year - 2024 }}
      title={title + " - " + year.toString()}
    />,
    handleCloseFunction,
    inputsArrayIndex,
    indexOfState,
  );
};

/**
 * Slices an array of outputs into subdivided array of values.
 * @param outputArray The array of outputs from the spreadsheet API.
 * @param kpisToSearch The selected KPIs.
 * @returns An array of the type chartGroupData to be ingested by a helper function that draws the charts.
 */

export const divideOutputsIntoChartGroupDataArray = (
  outputArray: OutputItem[],
  kpisToSearch: kpiMetadata[],
): chartGroupData[] => {
  var result: chartGroupData[] = [];
  var initialSliceIndex: number = 0;
  var finalSliceIndex: number = 0;

  kpisToSearch.map((currentKPI: kpiMetadata) => {
    var dataForKPI: chartGroupData = {
      name: currentKPI.kpiName,
      data: [],
      metadata: currentKPI.chartInfo,
    };

    currentKPI.rowData.map((dataLocationForIndividualChart: dataLocation) => {
      var dataForChart: chartData = [];
      var step =
        dataLocationForIndividualChart.finalDynamicValue +
        1 -
        dataLocationForIndividualChart.initialDynamicValue;
      finalSliceIndex += step;
      dataForChart = outputArray
        .slice(initialSliceIndex, finalSliceIndex)
        .map((outputItem) => {
          return outputItem.Value;
        });
      initialSliceIndex += step;

      dataForKPI.data.push(dataForChart);
    });

    result.push(dataForKPI);
  });

  return result;
};

export const CelinkFinancialModelingKPI = (
  kpi: chartGroupData,
  xAxisLabels: string[],
  metadata: chartInfo,
  /* formatArray: DataEncoding[],
  KPIChartNameArray: string[], */
  displayFlag: boolean,
) => {
  var plotDivs: JSX.Element[] = [];

  for (let i = 0; i < kpi.data.length; i = i + 2) {
    plotDivs.push(
      <div style={{ padding: "5px", display: "flex" }}>
        {genericPlot(
          kpi.data[i],
          xAxisLabels,
          metadata.chartFormat[i],
          metadata.chartNames[i],
        )}
        {genericPlot(
          kpi.data[i + 1],
          xAxisLabels,
          metadata.chartFormat[i + 1],
          metadata.chartNames[i + 1],
        )}
      </div>,
    );
  }

  return (
    <div
      className="bordered-object-no-width-restriction"
      style={{ display: displayFlag ? "block" : "none" }}
    >
      <div className="text-center-for-titles">
        {[...plKPIGroups, ...bsKPIGroups].find(
          (title) => title.value === kpi.name,
        )?.label ?? ""}
      </div>
      <div className="dollars-in-m">($ in M, unless stated otherwise)</div>
      {plotDivs}
    </div>
  );
};
